import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DEFAULT_LANGUAGE, LANGUAGES } from './languages';
import { translations } from './translations';
import { setGlobalTranslator } from './globalTranslator';
import { setGlobalLocale } from './globalLocale';

const STORAGE_KEY = 'logiflow.language';

const TranslationContext = createContext(null);

const formatWithValues = (template, values) => {
  if (!template || !values) return template;
  return template.replace(/{{(.*?)}}/g, (_, token) => {
    const key = token.trim();
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      const value = values[key];
      return value === undefined || value === null ? '' : `${value}`;
    }
    // Keep unresolved placeholders visible to avoid silently hiding missing data.
    return `{{${key}}}`;
  });
};

const normalizeOptions = (options) => {
  if (!options) return { values: undefined, fallback: undefined };
  if (
    Object.prototype.hasOwnProperty.call(options, 'values') ||
    Object.prototype.hasOwnProperty.call(options, 'fallback')
  ) {
    return {
      values: options.values,
      fallback: options.fallback,
    };
  }
  return { values: options, fallback: undefined };
};

export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored && translations[stored] ? stored : DEFAULT_LANGUAGE;
  });

  const translate = useCallback(
    (key, options) => {
      if (!key && key !== 0) return '';
      const { values, fallback } = normalizeOptions(options);
      const localePack = translations[language] || {};
      const defaultPack = translations[DEFAULT_LANGUAGE] || {};
      const template =
        localePack[key] ?? defaultPack[key] ?? fallback ?? (typeof key === 'string' ? key : `${key}`);
      return formatWithValues(template, values);
    },
    [language]
  );

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, language);
    }
  }, [language]);

  useEffect(() => {
    setGlobalLocale(language);
  }, [language]);

  useEffect(() => {
    setGlobalTranslator((value, options) => translate(value, options));
  }, [translate]);

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      languages: LANGUAGES,
      t: translate,
    }),
    [language, translate]
  );
  
  return <TranslationContext.Provider value={value}>{children}</TranslationContext.Provider>;
};

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

